// frontend/src/modules/historyTaskHandlers.js
import { DEFAULT_FILTERS, DOCUMENT_TYPE, NO_LEVEL_KEY, STEP, UPLOAD_MODE } from './workflowConfig';
import { getJson } from './apiClient';
import { hasRows } from '../utils/matchers';
import { mergeBatchResultItems, normalizeSingleResultTables } from './tableNormalization';

const TASK_TYPE_BY_MODE = {
  device: ['device_calibration_batch', 'device_calibration'],
  car: ['car_table', 'car_single_page', 'car_directory'],
  all: ['car_table', 'device_calibration_batch', 'device_calibration', 'car_single_page', 'car_directory'],
};

const resolveCarDocumentType = (requestPayload = {}) =>
  // 根据任务参数识别车辆文档类型。
  requestPayload?.document_type === DOCUMENT_TYPE.ENTERPRISE ? DOCUMENT_TYPE.ENTERPRISE : DOCUMENT_TYPE.STANDARD;

const createHistoryTaskContext = (detail = {}) => ({
  // 生成历史任务回放上下文信息。
  taskId: detail?.id || null,
  taskType: detail?.task_type || '',
});

export const resolveHistoryTaskTypeOptions = ({ isDeviceMode, isCarMode }) => {
  // 根据当前模式选择需要查询的任务类型集合。
  if (isDeviceMode) return TASK_TYPE_BY_MODE.device;
  if (isCarMode) return TASK_TYPE_BY_MODE.car;
  return TASK_TYPE_BY_MODE.all;
};

const shouldKeepTaskByDocumentType = (task, documentType) => {
  // 按文档类型过滤历史任务，避免模式混入。
  if (!documentType || !task) return true;

  const taskDocType = task.document_type;

  if (documentType === DOCUMENT_TYPE.ENTERPRISE) {
    return taskDocType === DOCUMENT_TYPE.ENTERPRISE;
  }

  if (documentType === DOCUMENT_TYPE.STANDARD) {
    return taskDocType !== DOCUMENT_TYPE.ENTERPRISE;
  }

  return true;
};

export const applyHistoryTaskResult = ({ detail, currentState, updateState }) => {
  // 将历史任务详情回放为页面当前状态。
  const payload = detail?.result_payload || {};
  const requestPayload = detail?.request_payload || {};
  const historyDirectory = requestPayload?.directory || '';

  if (detail.task_type === 'device_calibration') {
    const normalizedTables = normalizeSingleResultTables(payload?.result);
    updateState({
      documentType: DOCUMENT_TYPE.DEVICE,
      deviceType: detail?.request_payload?.device_type || currentState.deviceType,
      tables: normalizedTables,
      currentStep: STEP.RESULTS,
      historyTaskContext: createHistoryTaskContext(detail),
      error: '',
    });
    return;
  }

  if (detail.task_type === 'device_calibration_batch') {
    const normalizedTables = mergeBatchResultItems(payload?.items);
    updateState({
      documentType: DOCUMENT_TYPE.DEVICE,
      deviceType: detail?.request_payload?.device_type || currentState.deviceType,
      tables: normalizedTables,
      currentStep: STEP.RESULTS,
      historyTaskContext: createHistoryTaskContext(detail),
      error: normalizedTables.length ? '' : '历史批量任务存在，但无可展示表格数据',
    });
    return;
  }

  if (detail.task_type === 'car_table') {
    const levelResults = payload?.level_results || {};
    const selectedLevel = detail?.request_payload?.level || currentState.selectedLevel || DEFAULT_FILTERS.selectedLevel;
    const fallbackLevel = Object.keys(levelResults).find((level) => hasRows(levelResults[level]));
    const fallbackResult = Array.isArray(payload?.result) ? payload.result : [];
    const activeLevel = hasRows(levelResults?.[selectedLevel])
      ? selectedLevel
      : (fallbackLevel || (fallbackResult.length ? NO_LEVEL_KEY : selectedLevel));
    const activeTables = Array.isArray(levelResults?.[activeLevel])
      ? levelResults[activeLevel]
      : fallbackResult;
    const finalLevelResults = Object.keys(levelResults).length
      ? levelResults
      : (fallbackResult.length ? { [NO_LEVEL_KEY]: fallbackResult } : {});

    updateState({
      documentType: resolveCarDocumentType(detail?.request_payload),
      selectedLevel: activeLevel,
      carTablesByLevel: finalLevelResults,
      tables: activeTables,
      currentStep: STEP.RESULTS,
      historyTaskContext: createHistoryTaskContext(detail),
      error: '',
    });
    return;
  }

  if (detail.task_type === 'car_directory') {
    const directoryInfo = Array.isArray(detail?.car_directory_items)
      ? detail.car_directory_items
      : (Array.isArray(payload?.directory_info) ? payload.directory_info : []);

    const tableItems = Array.isArray(detail?.car_table_items) ? detail.car_table_items : [];
    const availableTables = tableItems.length
      ? tableItems.map((item) => item?.extracted_json || null).filter(Boolean)
      : (Array.isArray(payload?.tables) ? payload.tables : []);

    updateState({
      documentType: resolveCarDocumentType(detail?.request_payload),
      uploadMode: UPLOAD_MODE.FULL,
      directoryInfo,
      availableTables,
      selectedSinglePageDirectory: '',
      selectedDirectory: historyDirectory,
      tableHtml: '',
      currentStep: STEP.SELECT_DIRECTORY,
      historyTaskContext: createHistoryTaskContext(detail),
      error: directoryInfo.length ? '' : '历史目录任务存在，但缺少可回溯目录数据',
    });
    return;
  }

  if (detail.task_type === 'car_single_page') {
    const levelResults = payload?.level_results || payload || {};
    const selectedLevel = currentState.selectedLevel || DEFAULT_FILTERS.selectedLevel;

    let activeLevel = selectedLevel;
    if (!hasRows(levelResults?.[activeLevel])) {
      const fallbackLevel = Object.keys(levelResults).find((level) => hasRows(levelResults[level]));
      if (fallbackLevel) {
        activeLevel = fallbackLevel;
      }
    }

    const fallbackResult = Array.isArray(payload?.result) ? payload.result : [];
    const activeTables = Array.isArray(levelResults?.[activeLevel])
      ? levelResults[activeLevel]
      : fallbackResult;
    const finalLevelResults = Object.keys(levelResults).length
      ? levelResults
      : (fallbackResult.length ? { [NO_LEVEL_KEY]: fallbackResult } : {});
    const finalSelectedLevel = activeTables.length ? activeLevel : NO_LEVEL_KEY;

    updateState({
      documentType: resolveCarDocumentType(detail?.request_payload),
      uploadMode: UPLOAD_MODE.SINGLE,
      selectedSinglePageDirectory: historyDirectory,
      selectedDirectory: historyDirectory,
      selectedLevel: finalSelectedLevel,
      carTablesByLevel: finalLevelResults,
      tables: activeTables,
      currentStep: STEP.RESULTS,
      historyTaskContext: createHistoryTaskContext(detail),
      error: activeTables.length ? '' : '历史任务存在，但无可展示表格数据',
    });
    return;
  }

  updateState({ error: '该历史任务类型暂不支持直接回放' });
};

export const loadHistoryTasks = async ({ isDeviceMode, isCarMode, documentType }) => {
  // 并行拉取历史任务列表并按文档类型排序过滤。
  const taskTypeOptions = resolveHistoryTaskTypeOptions({ isDeviceMode, isCarMode });
  const buildHistoryTaskUrl = (taskType) =>
    `/api/history-tasks?status=success&task_type=${encodeURIComponent(taskType)}&limit=100`;
  const taskResponses = await Promise.allSettled(
    taskTypeOptions.map((taskType) => getJson(buildHistoryTaskUrl(taskType)))
  );
  const allTasks = taskResponses.flatMap((taskResponse) => {
    if (taskResponse.status !== 'fulfilled') {
      return [];
    }
    const { response, data } = taskResponse.value;
    return (response.ok && data?.success && Array.isArray(data?.result)) ? data.result : [];
  });

  const filteredTasks = allTasks.filter((task) => shouldKeepTaskByDocumentType(task, documentType));
  filteredTasks.sort((a, b) => (b.id || 0) - (a.id || 0));
  return filteredTasks;
};

export const fetchHistoryTaskDetail = async (taskId) => {
  // 拉取单个历史任务详情并执行响应校验。
  const { response, data } = await getJson(`/api/history-task-detail/${taskId}`);
  if (!response.ok || !data?.success || !data?.result) {
    throw new Error(data?.error || '历史任务详情获取失败');
  }
  return data.result;
};